import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Box, Typography } from '@mui/material';
import { CourseWithPricingState } from '../../../../features/types';
import VideoLessonIcon from '../../../../assets/icons/video-icon.svg';
import OnlineCourseIcon from '../../../../assets/icons/star-icon.svg';
import CircleCheckIcon from '../../../../assets/icons/list-check.svg';
import UncheckedIcon from '../../../../assets/icons/list-minus.svg';
import KaspiIcon from '../../../../assets/icons/kaspi-icon.png';
import BookIcon from '../../../../assets/icons/gift-book.png';
import GiftIcon from '../../../../assets/icons/gift-icon.png';
import FireIcon from '../../../../assets/icons/Fire.svg';
import { useNavigate } from 'react-router-dom';
import { tone, ui } from '../../../../utils/uiTokens'

// Кнопка с тултипом
function ChooseButton({ disabled, onClick, inCart, children }: {
    disabled: boolean;
    onClick: () => void;
    inCart: boolean;
    children: React.ReactNode;
}) {
    return (
        <Box sx={{ position: 'relative' }}>
            <Button
                variant="contained"
                disabled={disabled}
                onClick={onClick}
                sx={{
                    width: '100%',
                    py: 1.5,
                    backgroundColor: inCart ? tone.ok.text : ui.pink,
                    '&:disabled': {
                        backgroundColor: ui.track,
                        color: ui.muted
                    }
                }}
            >
                {inCart ? 'В корзине' : children}
            </Button>
            {disabled && (
                <Box
                    sx={styles.disabledTooltip}
                >
                    Жоғарыдан пән таңдаңыз
                </Box>
            )}
        </Box>
    );
}

// Индексы преимуществ для разных тарифов
const GIFT_INDEXES = (featuresLength: number) => [featuresLength - 2, featuresLength - 1];
const VIDEO_ACTIVE = [0];
const ONLINE_MONTH_ACTIVE = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const ONLINE_YEAR_ACTIVE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

interface CourseCardProps {
    videoCourse: CourseWithPricingState | null;
    monthlyCourse: CourseWithPricingState | null;
    yearlyCourse: CourseWithPricingState | null;
    paymentType: string;
    setPaymentType: (type: string) => void;
    selectedSubjects: number[];
}

export default function CourseCard({
    videoCourse,
    monthlyCourse,
    yearlyCourse,
    paymentType,
    setPaymentType,
    selectedSubjects,
}: CourseCardProps) {
    const { formatMessage } = useIntl();
    const isDisabled = selectedSubjects.length === 0;
    const navigate = useNavigate();
    // Хелпер для перехода на checkout с нужными параметрами
    const goToCheckout = (course: CourseWithPricingState | null) => {
        const params = new URLSearchParams();
        params.set('courseId', course?.id?.toString() || '');

        selectedSubjects.forEach(id => {
            params.append('subjectIds', id.toString());
        });
        navigate(`/sell-course/checkout?${params.toString()}`);
    };

    const renderFeatures = (activeArr: number[]) => {

        const features = [
            formatMessage({ id: 'courseFeatures-1' }),
            formatMessage({ id: 'courseFeatures-2' }),
            formatMessage({ id: 'courseFeatures-3' }),
            formatMessage({ id: 'courseFeatures-4' }),
            formatMessage({ id: 'courseFeatures-5' }),
            formatMessage({ id: 'courseFeatures-6' }),
            formatMessage({ id: 'courseFeatures-7' }),
            formatMessage({ id: 'courseFeatures-8' }),
            formatMessage({ id: 'courseFeatures-9' }),
            formatMessage({ id: 'courseFeatures-10' }),
            formatMessage({ id: 'courseFeatures-11' }),
            formatMessage({ id: 'courseFeatures-12' }),
        ];
        return features.map((text, i) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }} key={i}>
                {GIFT_INDEXES(features.length).includes(i)
                    ? <img src={GiftIcon} alt="gift" width={20} height={20} />
                    : activeArr.includes(i)
                        ? <img src={CircleCheckIcon} alt="check" width={20} height={20} />
                        : <img src={UncheckedIcon} alt="empty" width={20} height={20} />}
                <Typography variant="body2">{text}</Typography>
            </Box>
        ));
    };

    const DISCOUNT_HINTS = {
        onlineMonth: {
            1: { percent: 30, text: 'pricing-two_subjects' },
            2: { percent: 40, text: 'pricing-three_subjects' },
        },
        onlineYear: {
            1: { percent: 55, text: 'pricing-two_subjects' },
            2: { percent: 55, text: 'pricing-three_subjects' },
        },
    };

    const renderDiscountHint = (type: 'onlineMonth' | 'onlineYear') => {
        const hint = DISCOUNT_HINTS[type]?.[selectedSubjects.length];
        if (!hint) return null;

        return (
            <Box sx={styles.discountHint} key={hint.text}>
                <img src={FireIcon} alt="discount" height={32} />
                <Typography variant="body2" color={ui.sub}>
                    {formatMessage({ id: hint.text })}<br />
                    <Typography variant="body2" color={ui.ink} fontWeight={600}>
                        -{hint.percent}% {formatMessage({ id: '_discount' })}
                    </Typography>
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={styles.cardsContainer}>
            {/* Видеосабақ */}
            <Box
                sx={styles.card}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={VideoLessonIcon} alt="video" width={24} height={24} />
                        <Typography variant="h6">{formatMessage({ id: 'videoLesson' })}</Typography>
                    </Box>
                    <img src={KaspiIcon} alt="kaspi" height={32} />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                            variant={paymentType === 'monthly' ? 'contained' : 'outlined'}
                            onClick={() => setPaymentType('monthly')}
                            sx={styles.paymentButton}
                        >
                            <Typography variant="caption" display="block">
                                {formatMessage({ id: 'pricing-month_label' })}
                            </Typography>
                            <Typography variant="h6">{videoCourse?.monthly || 11000} ₸</Typography>
                        </Button>
                        <Button
                            variant={paymentType === 'yearly' ? 'contained' : 'outlined'}
                            onClick={() => setPaymentType('yearly')}
                            sx={styles.paymentButton}
                        >
                            <Typography variant="caption" display="block">
                                {formatMessage({ id: 'pricing-year_label' })}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: ui.muted }}>{videoCourse?.yearlyWithoutDiscount || 132000} ₸</Typography>
                                <Typography variant="h6">{videoCourse?.yearly || 33000} ₸</Typography>
                            </Box>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <img src={CircleCheckIcon} alt="check" width={20} height={20} />
                        <Typography variant="body2">
                            {formatMessage({ id: 'pricing-features-video_main' })}
                        </Typography>
                    </Box>
                    {renderFeatures(VIDEO_ACTIVE)}
                </Box>

                <ChooseButton
                    disabled={isDisabled}
                    inCart={false}
                    onClick={() => goToCheckout(videoCourse)}
                >
                    {formatMessage({ id: 'pricing-choose' })}
                </ChooseButton>
            </Box>

            {/* Онлайн курс (месяц) */}
            <Box
                sx={styles.card}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={OnlineCourseIcon} alt="online" width={24} height={24} />
                        <Typography variant="h6">{formatMessage({ id: 'onlineCourse' })}</Typography>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: ui.muted }}>
                        {monthlyCourse?.monthlyWithoutDiscount || 40000} ₸
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h5" color="primary">
                            {monthlyCourse?.monthly || 25000} ₸
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatMessage({ id: 'pricing-price_per_month' })}
                        </Typography>
                    </Box>
                </Box>

                {renderDiscountHint('onlineMonth')}

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <img src={CircleCheckIcon} alt="check" width={20} height={20} />
                        <Typography variant="body2">
                            {formatMessage({ id: 'pricing-features-online_month_main' })}
                        </Typography>
                    </Box>
                    {renderFeatures(ONLINE_MONTH_ACTIVE)}
                </Box>

                <ChooseButton
                    disabled={isDisabled}
                    inCart={false}
                    onClick={() => goToCheckout(monthlyCourse)}
                >
                    {formatMessage({ id: 'pricing-choose' })}
                </ChooseButton>
            </Box>

            {/* Онлайн курс (год) */}
            <Box
                sx={{ ...styles.card, ...styles.lastCard }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={OnlineCourseIcon} alt="premium" width={24} height={24} />
                        <Typography variant="h6">{formatMessage({ id: 'onlineCourse' })}</Typography>
                    </Box>
                    <img src={KaspiIcon} alt="kaspi" height={32} />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: ui.muted }}>
                        {yearlyCourse?.yearlyWithoutDiscount || 275000} ₸
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h5" color="primary">
                            {yearlyCourse?.yearly || 125000} ₸
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatMessage({ id: 'pricing-price_per_year' })}
                        </Typography>
                    </Box>
                </Box>

                {renderDiscountHint('onlineYear')}

                <Box sx={styles.banner}>
                    <img src={BookIcon} alt="books" height={90} />
                    <Box sx={styles.bannerText}>
                        <Typography variant="body2" color={ui.sub}>{formatMessage({ id: 'pricing-banner-line1' })}</Typography>
                        <Typography variant="h6" color={ui.pink}>{formatMessage({ id: 'pricing-banner-brand' })}</Typography>
                        <Typography variant="body2" color={ui.sub}>{formatMessage({ id: 'pricing-banner-line2' })}</Typography>
                    </Box>
                    <img src={GiftIcon} alt="discount" height={50} />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <img src={CircleCheckIcon} alt="check" width={20} height={20} />
                        <Typography variant="body2">
                            {formatMessage({ id: 'pricing-features-online_year_main' })}
                        </Typography>
                    </Box>
                    {renderFeatures(ONLINE_YEAR_ACTIVE)}
                </Box>

                <ChooseButton
                    disabled={isDisabled}
                    inCart={false}
                    onClick={() => goToCheckout(yearlyCourse)}
                >
                    {formatMessage({ id: 'pricing-choose' })}
                </ChooseButton>
            </Box>
        </Box>
    );
}


const styles = {
    card: {
        flex: 1,
        height: '100%',
        minWidth: '300px',
        maxWidth: '450px',
        p: 3,
        borderRadius: 2,
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        transition: 'all 0.3s',
    },
    lastCard: {
        border: `2px solid ${ui.pink}`,
        backgroundColor: ui.pinkSoft,
    },
    discountHint: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        background: tone.warn.bg,
        border: `1px solid ${tone.warn.border}`,
        borderRadius: 2,
        p: 1,
        mb: 2,
        width: 'fit-content',
    },
    disabledTooltip: {
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        mb: 1,
        p: 1,
        backgroundColor: ui.ink,
        color: '#fff',
        borderRadius: 1,
        fontSize: '12px',
    },
    banner: {
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${ui.pink}`,
        backgroundColor: ui.pinkSoft,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    bannerText: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
    },
    cardsContainer: {
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        mt: 3,
    },
    paymentButton: {
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        py: 1,
    },
}